import { useCallback, useEffect, useState } from "react";

export const SAVED_STORAGE_KEY = "mj-saved-activities";
export const SAVED_CHANGED_EVENT = "mj-saved-changed";

export interface LocalSavedItem {
  slug: string;
  title: string;
  city: string;
  price: number;
  currency: string;
  priceType?: string | number;
  image?: string;
  category?: string;
  savedAt: number;
}

function isLocalSavedItem(x: unknown): x is LocalSavedItem {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.slug === "string" &&
    typeof o.title === "string" &&
    typeof o.city === "string" &&
    typeof o.price === "number" &&
    typeof o.currency === "string" &&
    typeof o.savedAt === "number"
  );
}

export function readSaved(): LocalSavedItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(SAVED_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isLocalSavedItem);
  } catch {
    return [];
  }
}

export function writeSaved(items: LocalSavedItem[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(SAVED_STORAGE_KEY, JSON.stringify(items));
    window.dispatchEvent(new CustomEvent(SAVED_CHANGED_EVENT));
  } catch {
    // ignore quota / serialization failures
  }
}

export function isSaved(slug: string): boolean {
  return readSaved().some((i) => i.slug === slug);
}

export function toggleSavedItem(item: Omit<LocalSavedItem, "savedAt">): boolean {
  const list = readSaved();
  const idx = list.findIndex((i) => i.slug === item.slug);
  if (idx >= 0) {
    list.splice(idx, 1);
    writeSaved(list);
    return false;
  }
  list.unshift({ ...item, savedAt: Date.now() });
  writeSaved(list);
  return true;
}

export function removeSaved(slug: string): void {
  writeSaved(readSaved().filter((i) => i.slug !== slug));
}

export function useLocalSaved(): LocalSavedItem[] {
  const [items, setItems] = useState<LocalSavedItem[]>([]);
  useEffect(() => {
    const sync = () => setItems(readSaved());
    sync();
    const onStorage = (e: StorageEvent) => {
      if (e.key === SAVED_STORAGE_KEY) sync();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener(SAVED_CHANGED_EVENT, sync as EventListener);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(SAVED_CHANGED_EVENT, sync as EventListener);
    };
  }, []);
  return items;
}

export function useIsSaved(slug: string): [boolean, () => void] {
  const items = useLocalSaved();
  const saved = items.some((i) => i.slug === slug);
  const remove = useCallback(() => removeSaved(slug), [slug]);
  return [saved, remove];
}
